import {
  DEFAULT_TABLE_LARGE_DEPTH_CM,
  DEFAULT_TABLE_LARGE_WIDTH_CM,
  DEFAULT_TABLE_MEDIUM_DEPTH_CM,
  DEFAULT_TABLE_MEDIUM_WIDTH_CM,
  DEFAULT_TABLE_SMALL_DEPTH_CM,
  DEFAULT_TABLE_SMALL_WIDTH_CM,
  DEFAULT_WALL_THICKNESS_CM,
  FloorplanDecorKind,
  FloorplanLightingPreset,
  FloorplanObjectType,
} from '../constants/venueLayout'
import {
  VenueLayoutTableSize,
  VenueLayoutTableType,
} from '../constants/venueLayoutTable'
import type { FloorplanDocument, FloorplanDocumentObject } from '../venueLayout'
import type { VenueLayoutTable } from '../venueLayoutTable'
import {
  cmToCanvasUnits,
  getWallHeightCanvas,
  getWallThicknessCanvas,
} from './floorplanMeasurementUtils'

export type IsoProjectionPoint = {
  x: number
  y: number
}

export type IsoProjectionInput = {
  x: number
  y: number
  z?: number
}

export type IsoProjectionConfig = {
  /** Orbit angle around the document Z axis (degrees), applied before projection. */
  azimuthDegrees?: number
  /** Camera elevation above the floor plane (degrees). Alias: angleDegrees. */
  elevationDegrees?: number
  /** @deprecated Use elevationDegrees. Kept for backward compatibility. */
  angleDegrees?: number
  /** Wall height foreshortening. Defaults to sin(elevation) / sin(30°). */
  verticalScale?: number
  /** Document-space pivot for azimuth rotation. Defaults to origin. */
  pivotX?: number
  pivotY?: number
}

export const DEFAULT_ISO_AZIMUTH_DEGREES = 0
export const DEFAULT_ISO_ELEVATION_DEGREES = 30
export const DEFAULT_ISO_VERTICAL_SCALE = 1
export type RenderableWallLine = {
  id: string
  roomId: string
  points: [number, number, number, number]
  thickness: number
  elevation: number
  implicit: boolean
  wallColor?: string | null
  wallMaterial?: FloorplanDocument['shell']['rooms'][number]['walls'][number]['wallMaterial']
}
export type WallSegment = [number, number, number, number]
export type FloorplanParentRef = {
  parentId?: string | null
  parentKind?: 'room' | null
}
export type SnapWallSegmentOptions = {
  threshold?: number
  snapStart?: boolean
  snapEnd?: boolean
  preserveLength?: boolean
}
export type SnapWallSegmentResult = {
  points: WallSegment
  startSnapped: boolean
  endSnapped: boolean
}
export type RenderLayerName =
  | 'background'
  | 'lighting'
  | 'rooms'
  | 'sections'
  | 'walls'
  | 'decor'
  | 'tables'
  | 'labels'

export const FLOORPLAN_RENDER_LAYER_ORDER: RenderLayerName[] = [
  'background',
  'lighting',
  'rooms',
  'sections',
  'walls',
  'decor',
  'tables',
  'labels',
]

export type FloorTexturePattern = 'none' | 'diagonal' | 'crosshatch' | 'checker'
export type FloorTexturePalette = {
  pattern: FloorTexturePattern
  backgroundFrom: string
  backgroundTo: string
  overlayPrimary: string
  overlaySecondary: string
  overlayOpacity: number
  step: number
}
export type TextureOverlayLine = {
  points: WallSegment
  secondary?: boolean
}
export type FloorplanTableGeometryDescriptor = {
  shape: VenueLayoutTableType
  width: number
  depth: number
  cornerRadius: number
  height: number
}
export type FloorplanDecorVisualDescriptor = {
  width: number
  height: number
  height3d: number
  topFill: string
  sideFill: string
  stroke: string
  canopyFill?: string
  canopyStroke?: string
  trunkFill?: string
  accentFill?: string
}

export const DEFAULT_WALL_SNAP_THRESHOLD = 24

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function floorTextureKey(texture: string | null | undefined): string {
  return texture?.trim().toLowerCase() ?? ''
}

function resolveLightingPresetColor(preset: FloorplanLightingPreset): string {
  switch (preset) {
    case FloorplanLightingPreset.COOL:
      return '#60a5fa'
    case FloorplanLightingPreset.CLUB:
      return '#a855f7'
    case FloorplanLightingPreset.VIP:
      return '#8b5cf6'
    case FloorplanLightingPreset.DARK:
      return '#312e81'
    default:
      return '#f59e0b'
  }
}

function getPolylineSegments(points: number[]): WallSegment[] {
  const segments: WallSegment[] = []
  for (let index = 0; index <= points.length - 4; index += 2) {
    const x1 = points[index] ?? 0
    const y1 = points[index + 1] ?? 0
    const x2 = points[index + 2] ?? 0
    const y2 = points[index + 3] ?? 0
    if (x1 === x2 && y1 === y2) continue
    segments.push([x1, y1, x2, y2])
  }
  return segments
}

function getPolygonEdgeSegments(points: number[]): WallSegment[] {
  const segments: WallSegment[] = []
  const pointCount = Math.floor(points.length / 2)
  if (pointCount < 2) return segments
  for (let pointIndex = 0; pointIndex < pointCount; pointIndex += 1) {
    const nextIndex = (pointIndex + 1) % pointCount
    const x1 = points[pointIndex * 2] ?? 0
    const y1 = points[pointIndex * 2 + 1] ?? 0
    const x2 = points[nextIndex * 2] ?? 0
    const y2 = points[nextIndex * 2 + 1] ?? 0
    if (x1 === x2 && y1 === y2) continue
    segments.push([x1, y1, x2, y2])
  }
  return segments
}

export function getRoomLocalOrigin(
  room: FloorplanDocument['shell']['rooms'][number]
): { x: number; y: number } {
  return { x: room.x, y: room.y }
}

function getRoomById(
  doc: FloorplanDocument,
  roomId: string | null | undefined
): FloorplanDocument['shell']['rooms'][number] | null {
  if (!roomId) return null
  return doc.shell.rooms.find((room) => room.id === roomId) ?? null
}

export function resolveWorldPointFromParent(args: {
  doc: FloorplanDocument
  point: { x: number; y: number }
  parentId?: string | null
  parentKind?: 'room' | null
}): { x: number; y: number } {
  if (!args.parentId || args.parentKind !== 'room') return args.point
  const room = getRoomById(args.doc, args.parentId)
  if (!room) return args.point
  const origin = getRoomLocalOrigin(room)
  return {
    x: origin.x + args.point.x,
    y: origin.y + args.point.y,
  }
}

export function resolveLocalPointFromParent(args: {
  doc: FloorplanDocument
  point: { x: number; y: number }
  parentId?: string | null
  parentKind?: 'room' | null
}): { x: number; y: number } {
  if (!args.parentId || args.parentKind !== 'room') return args.point
  const room = getRoomById(args.doc, args.parentId)
  if (!room) return args.point
  const origin = getRoomLocalOrigin(room)
  return {
    x: args.point.x - origin.x,
    y: args.point.y - origin.y,
  }
}

export function resolveWorldSegmentFromParent(args: {
  doc: FloorplanDocument
  points: WallSegment
  parentId?: string | null
  parentKind?: 'room' | null
}): WallSegment {
  const start = resolveWorldPointFromParent({
    doc: args.doc,
    point: { x: args.points[0], y: args.points[1] },
    parentId: args.parentId,
    parentKind: args.parentKind,
  })
  const end = resolveWorldPointFromParent({
    doc: args.doc,
    point: { x: args.points[2], y: args.points[3] },
    parentId: args.parentId,
    parentKind: args.parentKind,
  })
  return [start.x, start.y, end.x, end.y]
}

export function resolveLocalSegmentFromParent(args: {
  doc: FloorplanDocument
  points: WallSegment
  parentId?: string | null
  parentKind?: 'room' | null
}): WallSegment {
  const start = resolveLocalPointFromParent({
    doc: args.doc,
    point: { x: args.points[0], y: args.points[1] },
    parentId: args.parentId,
    parentKind: args.parentKind,
  })
  const end = resolveLocalPointFromParent({
    doc: args.doc,
    point: { x: args.points[2], y: args.points[3] },
    parentId: args.parentId,
    parentKind: args.parentKind,
  })
  return [start.x, start.y, end.x, end.y]
}

function projectPointToSegment(
  point: { x: number; y: number },
  segment: WallSegment
): { x: number; y: number; distance: number } {
  const [x1, y1, x2, y2] = segment
  const dx = x2 - x1
  const dy = y2 - y1
  const lengthSq = dx * dx + dy * dy
  if (lengthSq <= 0.0001) {
    const distance = Math.hypot(point.x - x1, point.y - y1)
    return { x: x1, y: y1, distance }
  }
  const t = Math.max(
    0,
    Math.min(1, ((point.x - x1) * dx + (point.y - y1) * dy) / lengthSq)
  )
  const x = x1 + t * dx
  const y = y1 + t * dy
  return {
    x,
    y,
    distance: Math.hypot(point.x - x, point.y - y),
  }
}

function getClosestPointOnSegments(
  point: { x: number; y: number },
  segments: WallSegment[],
  threshold: number
): { x: number; y: number; distance: number; snapped: boolean } {
  let bestPoint = { x: point.x, y: point.y }
  let bestDistance = Number.POSITIVE_INFINITY

  for (const segment of segments) {
    const projected = projectPointToSegment(point, segment)
    if (projected.distance <= threshold && projected.distance < bestDistance) {
      bestDistance = projected.distance
      bestPoint = { x: projected.x, y: projected.y }
    }
  }

  return {
    x: bestPoint.x,
    y: bestPoint.y,
    distance: bestDistance,
    snapped: Number.isFinite(bestDistance),
  }
}

export function getRoomBoundarySegments(doc: FloorplanDocument): WallSegment[] {
  return doc.shell.rooms.flatMap((room) =>
    getPolygonEdgeSegments(room.polygon).map(
      ([x1, y1, x2, y2]): WallSegment => [
        room.x + x1,
        room.y + y1,
        room.x + x2,
        room.y + y2,
      ]
    )
  )
}

export function snapWallSegmentToBoundaries(
  points: WallSegment,
  boundaries: WallSegment[],
  options: SnapWallSegmentOptions = {}
): SnapWallSegmentResult {
  const threshold = options.threshold ?? DEFAULT_WALL_SNAP_THRESHOLD
  const snapStart = options.snapStart ?? true
  const snapEnd = options.snapEnd ?? true
  const preserveLength = options.preserveLength ?? false
  const [x1, y1, x2, y2] = points
  const startPoint = { x: x1, y: y1 }
  const endPoint = { x: x2, y: y2 }

  const nearestStart = snapStart
    ? getClosestPointOnSegments(startPoint, boundaries, threshold)
    : { x: x1, y: y1, distance: Number.POSITIVE_INFINITY, snapped: false }
  const nearestEnd = snapEnd
    ? getClosestPointOnSegments(endPoint, boundaries, threshold)
    : { x: x2, y: y2, distance: Number.POSITIVE_INFINITY, snapped: false }

  if (preserveLength && (nearestStart.snapped || nearestEnd.snapped)) {
    const useStart =
      nearestStart.snapped &&
      (!nearestEnd.snapped || nearestStart.distance <= nearestEnd.distance)
    const anchor = useStart ? nearestStart : nearestEnd
    const anchorSource = useStart ? startPoint : endPoint
    const dx = anchor.x - anchorSource.x
    const dy = anchor.y - anchorSource.y
    return {
      points: [x1 + dx, y1 + dy, x2 + dx, y2 + dy],
      startSnapped: nearestStart.snapped,
      endSnapped: nearestEnd.snapped,
    }
  }

  return {
    points: [
      nearestStart.snapped ? nearestStart.x : x1,
      nearestStart.snapped ? nearestStart.y : y1,
      nearestEnd.snapped ? nearestEnd.x : x2,
      nearestEnd.snapped ? nearestEnd.y : y2,
    ],
    startSnapped: nearestStart.snapped,
    endSnapped: nearestEnd.snapped,
  }
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

function resolveIsoElevationDegrees(config: IsoProjectionConfig): number {
  return (
    config.elevationDegrees ??
    config.angleDegrees ??
    DEFAULT_ISO_ELEVATION_DEGREES
  )
}

function resolveIsoVerticalScale(
  config: IsoProjectionConfig,
  elevationRadians: number
): number {
  if (config.verticalScale !== undefined) return config.verticalScale
  const defaultElevationRadians = toRadians(DEFAULT_ISO_ELEVATION_DEGREES)
  return Math.sin(elevationRadians) / Math.sin(defaultElevationRadians)
}

export function isoProject(
  point: IsoProjectionInput,
  config: IsoProjectionConfig = {}
): IsoProjectionPoint {
  const elevation = toRadians(resolveIsoElevationDegrees(config))
  const azimuth = toRadians(
    config.azimuthDegrees ?? DEFAULT_ISO_AZIMUTH_DEGREES
  )
  const pivotX = config.pivotX ?? 0
  const pivotY = config.pivotY ?? 0
  const verticalScale = resolveIsoVerticalScale(config, elevation)
  const z = point.z ?? 0

  const localX = point.x - pivotX
  const localY = point.y - pivotY
  const rotatedX =
    localX * Math.cos(azimuth) - localY * Math.sin(azimuth) + pivotX
  const rotatedY =
    localX * Math.sin(azimuth) + localY * Math.cos(azimuth) + pivotY

  const isoX = (rotatedX - rotatedY) * Math.cos(elevation)
  const isoY = (rotatedX + rotatedY) * Math.sin(elevation) - z * verticalScale

  return { x: isoX, y: isoY }
}

export function isoScreenDeltaToWorldDelta(args: {
  deltaX: number
  deltaY: number
  config?: IsoProjectionConfig
}): IsoProjectionPoint {
  const config = args.config ?? {}
  const origin = isoProject({ x: 0, y: 0, z: 0 }, config)
  const basisXPoint = isoProject({ x: 1, y: 0, z: 0 }, config)
  const basisYPoint = isoProject({ x: 0, y: 1, z: 0 }, config)

  const m00 = basisXPoint.x - origin.x
  const m01 = basisYPoint.x - origin.x
  const m10 = basisXPoint.y - origin.y
  const m11 = basisYPoint.y - origin.y
  const determinant = m00 * m11 - m01 * m10

  if (Math.abs(determinant) < 1e-6) {
    return { x: 0, y: 0 }
  }

  const inverseScale = 1 / determinant
  return {
    x: (args.deltaX * m11 - args.deltaY * m01) * inverseScale,
    y: (args.deltaY * m00 - args.deltaX * m10) * inverseScale,
  }
}

export function flattenIsoPoints(points: IsoProjectionPoint[]): number[] {
  return points.flatMap((p) => [p.x, p.y])
}

export function projectPolygon(
  polygonFlat: number[],
  z: number,
  config: IsoProjectionConfig = {}
): IsoProjectionPoint[] {
  const points: IsoProjectionPoint[] = []
  for (let i = 0; i < polygonFlat.length; i += 2) {
    points.push(
      isoProject(
        {
          x: polygonFlat[i] ?? 0,
          y: polygonFlat[i + 1] ?? 0,
          z,
        },
        config
      )
    )
  }
  return points
}

export function resolveRoomWorldPolygon(
  room: FloorplanDocument['shell']['rooms'][number]
): number[] {
  return room.polygon.map((value, index) =>
    index % 2 === 0 ? value + room.x : value + room.y
  )
}

export function getFlatPolygonBounds(points: number[]): {
  minX: number
  minY: number
  width: number
  height: number
} | null {
  if (points.length < 4) return null
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY
  for (let index = 0; index < points.length - 1; index += 2) {
    const x = points[index]
    const y = points[index + 1]
    if (x == null || y == null) continue
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x)
    maxY = Math.max(maxY, y)
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null
  return {
    minX,
    minY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  }
}

/** Konva fillPatternTransform [a,b,c,d,e,f] mapping pattern pixels to iso screen space. */
export function resolveIsoFillPatternTransform(args: {
  config?: IsoProjectionConfig
  worldOrigin?: { x: number; y: number }
  /** World-space span represented by one pattern tile (canvas/doc units). */
  tileSpan?: { x: number; y: number }
  /** Loaded pattern image size in pixels (Konva pattern space). */
  patternSize?: { width: number; height: number }
}): number[] {
  const config = args.config ?? {}
  const worldOrigin = args.worldOrigin ?? { x: 0, y: 0 }
  const tileSpan = args.tileSpan ?? { x: 1, y: 1 }
  const patternWidth = Math.max(1, args.patternSize?.width ?? 1)
  const patternHeight = Math.max(1, args.patternSize?.height ?? patternWidth)
  const origin = isoProject(
    { x: worldOrigin.x, y: worldOrigin.y, z: 0 },
    config
  )
  const spanX = isoProject(
    { x: worldOrigin.x + tileSpan.x, y: worldOrigin.y, z: 0 },
    config
  )
  const spanY = isoProject(
    { x: worldOrigin.x, y: worldOrigin.y + tileSpan.y, z: 0 },
    config
  )
  return [
    (spanX.x - origin.x) / patternWidth,
    (spanX.y - origin.y) / patternWidth,
    (spanY.x - origin.x) / patternHeight,
    (spanY.y - origin.y) / patternHeight,
    origin.x,
    origin.y,
  ]
}

/** Konva fillPatternTransform [a,b,c,d,e,f] for wall side faces, using wall-local axes. */
export function resolveIsoWallSidePatternTransform(args: {
  config?: IsoProjectionConfig
  wallPoints: [number, number, number, number]
  /** World/canvas z at wall base. */
  wallBaseZ?: number
  /** Wall tile span in world/canvas units. x=length axis, y=height axis. */
  tileSpan?: { x: number; y: number }
  /** Loaded pattern image size in pixels (Konva pattern space). */
  patternSize?: { width: number; height: number }
}): number[] {
  const config = args.config ?? {}
  const [x1, y1, x2, y2] = args.wallPoints
  const length = Math.hypot(x2 - x1, y2 - y1)
  const ux = length > 0.0001 ? (x2 - x1) / length : 1
  const uy = length > 0.0001 ? (y2 - y1) / length : 0
  const tileSpan = args.tileSpan ?? { x: 1, y: 1 }
  const patternWidth = Math.max(1, args.patternSize?.width ?? 1)
  const patternHeight = Math.max(1, args.patternSize?.height ?? patternWidth)
  const baseZ = args.wallBaseZ ?? 0

  const origin = isoProject({ x: x1, y: y1, z: baseZ }, config)
  const spanU = isoProject(
    {
      x: x1 + ux * tileSpan.x,
      y: y1 + uy * tileSpan.x,
      z: baseZ,
    },
    config
  )
  const spanV = isoProject(
    {
      x: x1,
      y: y1,
      z: baseZ + tileSpan.y,
    },
    config
  )

  return [
    (spanU.x - origin.x) / patternWidth,
    (spanU.y - origin.y) / patternWidth,
    (spanV.x - origin.x) / patternHeight,
    (spanV.y - origin.y) / patternHeight,
    origin.x,
    origin.y,
  ]
}

export function getFloorplanTableObjects(
  doc: FloorplanDocument
): Extract<FloorplanDocumentObject, { type: FloorplanObjectType.TABLE }>[] {
  return doc.objects.filter(
    (
      objectRow: FloorplanDocumentObject
    ): objectRow is Extract<
      FloorplanDocumentObject,
      { type: FloorplanObjectType.TABLE }
    > => objectRow.type === FloorplanObjectType.TABLE
  )
}

export function getFloorplanSectionObjects(
  doc: FloorplanDocument
): Extract<FloorplanDocumentObject, { type: FloorplanObjectType.SECTION }>[] {
  return doc.objects.filter(
    (
      objectRow: FloorplanDocumentObject
    ): objectRow is Extract<
      FloorplanDocumentObject,
      { type: FloorplanObjectType.SECTION }
    > => objectRow.type === FloorplanObjectType.SECTION
  )
}

export function getFloorplanLabelObjects(
  doc: FloorplanDocument
): Extract<FloorplanDocumentObject, { type: FloorplanObjectType.LABEL }>[] {
  return doc.objects.filter(
    (
      objectRow: FloorplanDocumentObject
    ): objectRow is Extract<
      FloorplanDocumentObject,
      { type: FloorplanObjectType.LABEL }
    > => objectRow.type === FloorplanObjectType.LABEL
  )
}

export function getFloorplanDecorObjects(
  doc: FloorplanDocument
): Extract<FloorplanDocumentObject, { type: FloorplanObjectType.DECOR }>[] {
  return doc.objects.filter(
    (
      objectRow: FloorplanDocumentObject
    ): objectRow is Extract<
      FloorplanDocumentObject,
      { type: FloorplanObjectType.DECOR }
    > => objectRow.type === FloorplanObjectType.DECOR
  )
}

export function getFloorplanLightingZoneObjects(
  doc: FloorplanDocument
): Extract<
  FloorplanDocumentObject,
  { type: FloorplanObjectType.LIGHTING_ZONE }
>[] {
  return doc.objects.filter(
    (
      objectRow: FloorplanDocumentObject
    ): objectRow is Extract<
      FloorplanDocumentObject,
      { type: FloorplanObjectType.LIGHTING_ZONE }
    > => objectRow.type === FloorplanObjectType.LIGHTING_ZONE
  )
}

export function resolveLightingZoneColor(
  zone: Extract<
    FloorplanDocumentObject,
    { type: FloorplanObjectType.LIGHTING_ZONE }
  >
): string {
  return zone.color?.trim() || resolveLightingPresetColor(zone.preset)
}

export function resolveLightingZoneOpacity(
  zone: Extract<
    FloorplanDocumentObject,
    { type: FloorplanObjectType.LIGHTING_ZONE }
  >
): number {
  const base =
    zone.preset === FloorplanLightingPreset.DARK
      ? 0.16
      : zone.preset === FloorplanLightingPreset.VIP
        ? 0.2
        : zone.preset === FloorplanLightingPreset.CLUB
          ? 0.22
          : 0.18
  const intensity = zone.intensity == null ? 0.6 : clamp01(zone.intensity)
  return clamp01(base + intensity * 0.2)
}

export function resolveFloorTexturePalette(
  texture: string | null | undefined
): FloorTexturePalette {
  const key = floorTextureKey(texture)
  if (key.includes('wood')) {
    return {
      pattern: 'diagonal',
      backgroundFrom: '#2f211d',
      backgroundTo: '#1f1614',
      overlayPrimary: '#b08968',
      overlaySecondary: '#9c6644',
      overlayOpacity: 0.2,
      step: 22,
    }
  }
  if (key.includes('concrete') || key.includes('stone')) {
    return {
      pattern: 'crosshatch',
      backgroundFrom: '#2d3138',
      backgroundTo: '#1f232a',
      overlayPrimary: '#94a3b8',
      overlaySecondary: '#64748b',
      overlayOpacity: 0.22,
      step: 24,
    }
  }
  if (key.includes('checker')) {
    return {
      pattern: 'checker',
      backgroundFrom: '#31232a',
      backgroundTo: '#21181d',
      overlayPrimary: '#f59e0b',
      overlaySecondary: '#7c3aed',
      overlayOpacity: 0.16,
      step: 26,
    }
  }
  if (key.includes('marble')) {
    return {
      pattern: 'diagonal',
      backgroundFrom: '#2f3643',
      backgroundTo: '#1f2732',
      overlayPrimary: '#cbd5e1',
      overlaySecondary: '#93c5fd',
      overlayOpacity: 0.14,
      step: 28,
    }
  }
  return {
    pattern: 'none',
    backgroundFrom: '#2a2a31',
    backgroundTo: '#1f1f24',
    overlayPrimary: '#ffffff',
    overlaySecondary: '#cbd5e1',
    overlayOpacity: 0.1,
    step: 24,
  }
}

export function getFloorTextureOverlayLines({
  width,
  height,
  pattern,
  step,
}: {
  width: number
  height: number
  pattern: FloorTexturePattern
  step: number
}): TextureOverlayLine[] {
  if (pattern === 'none') return []
  const safeStep = Math.max(8, Math.floor(step))
  const lines: TextureOverlayLine[] = []

  if (pattern === 'crosshatch' || pattern === 'checker') {
    for (let x = 0; x <= width; x += safeStep) {
      lines.push({
        points: [x, 0, x, height],
        secondary: pattern === 'checker' && Math.floor(x / safeStep) % 2 === 0,
      })
    }
    for (let y = 0; y <= height; y += safeStep) {
      lines.push({
        points: [0, y, width, y],
        secondary: pattern === 'checker' && Math.floor(y / safeStep) % 2 === 1,
      })
    }
    return lines
  }

  const diagonalSpan = width + height
  for (let offset = -height; offset <= diagonalSpan; offset += safeStep) {
    lines.push({
      points: [offset, 0, offset + height, height],
      secondary: Math.floor((offset + height) / safeStep) % 2 === 0,
    })
  }
  return lines
}

export function resolveTableLabel(
  tableObject: Extract<
    FloorplanDocumentObject,
    { type: FloorplanObjectType.TABLE }
  >,
  tableById: Map<string, VenueLayoutTable>
): string {
  const objectName = tableObject.name?.trim()
  if (objectName) return objectName
  const linkedTable =
    tableObject.venueLayoutTableId != null
      ? tableById.get(tableObject.venueLayoutTableId)
      : undefined

  return linkedTable?.name ?? 'Table'
}

function resolveTableGeometrySize(size?: VenueLayoutTableSize | null): {
  widthCm: number
  depthCm: number
  height: number
} {
  if (size === VenueLayoutTableSize.LARGE) {
    return {
      widthCm: DEFAULT_TABLE_LARGE_WIDTH_CM,
      depthCm: DEFAULT_TABLE_LARGE_DEPTH_CM,
      height: 12,
    }
  }
  if (size === VenueLayoutTableSize.SMALL) {
    return {
      widthCm: DEFAULT_TABLE_SMALL_WIDTH_CM,
      depthCm: DEFAULT_TABLE_SMALL_DEPTH_CM,
      height: 8,
    }
  }
  return {
    widthCm: DEFAULT_TABLE_MEDIUM_WIDTH_CM,
    depthCm: DEFAULT_TABLE_MEDIUM_DEPTH_CM,
    height: 10,
  }
}

export function resolveTableGeometry(args: {
  tableObject: Extract<
    FloorplanDocumentObject,
    { type: FloorplanObjectType.TABLE }
  >
  doc: FloorplanDocument
  linkedTable?: VenueLayoutTable | null
}): FloorplanTableGeometryDescriptor {
  const shape = args.tableObject.shape ?? VenueLayoutTableType.ROUND
  const fromSize = resolveTableGeometrySize(args.tableObject.size)
  const width = Math.max(
    12,
    cmToCanvasUnits(args.tableObject.widthCm ?? fromSize.widthCm, args.doc)
  )
  const depth = Math.max(
    12,
    cmToCanvasUnits(args.tableObject.depthCm ?? fromSize.depthCm, args.doc)
  )
  const height = Math.max(4, Math.min(22, fromSize.height))
  return {
    shape,
    width,
    depth,
    height,
    cornerRadius: shape === VenueLayoutTableType.ROUND ? width / 2 : 4,
  }
}

export function getCenteredRectPolygon(args: {
  centerX: number
  centerY: number
  width: number
  height: number
}): number[] {
  const halfWidth = args.width / 2
  const halfHeight = args.height / 2
  return [
    args.centerX - halfWidth,
    args.centerY - halfHeight,
    args.centerX + halfWidth,
    args.centerY - halfHeight,
    args.centerX + halfWidth,
    args.centerY + halfHeight,
    args.centerX - halfWidth,
    args.centerY + halfHeight,
  ]
}

export function getCenteredEllipsePolygon(args: {
  centerX: number
  centerY: number
  radiusX: number
  radiusY: number
  segmentCount?: number
}): number[] {
  const segmentCount = Math.max(12, args.segmentCount ?? 20)
  const points: number[] = []
  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex += 1) {
    const theta = (segmentIndex / segmentCount) * Math.PI * 2
    points.push(
      args.centerX + Math.cos(theta) * args.radiusX,
      args.centerY + Math.sin(theta) * args.radiusY
    )
  }
  return points
}

export function getTableTopPolygon(args: {
  centerX: number
  centerY: number
  geometry: FloorplanTableGeometryDescriptor
}): number[] {
  if (args.geometry.shape === VenueLayoutTableType.SQUARE) {
    return getCenteredRectPolygon({
      centerX: args.centerX,
      centerY: args.centerY,
      width: args.geometry.width,
      height: args.geometry.depth,
    })
  }
  return getCenteredEllipsePolygon({
    centerX: args.centerX,
    centerY: args.centerY,
    radiusX: args.geometry.width / 2,
    radiusY: args.geometry.depth / 2,
    segmentCount: 24,
  })
}

export function resolveDecorVisualDescriptor(args: {
  decorKind: FloorplanDecorKind
  width?: number | null
  height?: number | null
}): FloorplanDecorVisualDescriptor {
  const width = Math.max(12, args.width ?? 28)
  const height = Math.max(12, args.height ?? width)
  switch (args.decorKind) {
    case FloorplanDecorKind.PLANT:
      return {
        width,
        height,
        height3d: 22,
        topFill: 'rgba(34,197,94,0.86)',
        sideFill: 'rgba(21,128,61,0.9)',
        stroke: 'rgba(20,83,45,0.95)',
        canopyFill: 'rgba(74,222,128,0.82)',
        canopyStroke: 'rgba(20,83,45,0.95)',
        trunkFill: 'rgba(120,53,15,0.95)',
        accentFill: 'rgba(255,255,255,0.35)',
      }
    case FloorplanDecorKind.ART:
      return {
        width,
        height,
        height3d: 6,
        topFill: 'rgba(244,114,182,0.28)',
        sideFill: 'rgba(190,24,93,0.42)',
        stroke: 'rgba(190,24,93,0.9)',
        accentFill: 'rgba(255,255,255,0.82)',
      }
    case FloorplanDecorKind.DJ_BOOTH:
      return {
        width,
        height,
        height3d: 12,
        topFill: 'rgba(15,23,42,0.9)',
        sideFill: 'rgba(15,23,42,0.76)',
        stroke: 'rgba(56,189,248,0.85)',
        accentFill: 'rgba(34,211,238,0.3)',
      }
    case FloorplanDecorKind.CURTAIN:
      return {
        width,
        height,
        height3d: 8,
        topFill: 'rgba(99,102,241,0.62)',
        sideFill: 'rgba(67,56,202,0.68)',
        stroke: 'rgba(199,210,254,0.76)',
        accentFill: 'rgba(238,242,255,0.4)',
      }
    case FloorplanDecorKind.BAR:
      return {
        width,
        height,
        height3d: 14,
        topFill: 'rgba(120,53,15,0.88)',
        sideFill: 'rgba(92,38,13,0.88)',
        stroke: 'rgba(245,158,11,0.82)',
        accentFill: 'rgba(253,230,138,0.4)',
      }
    case FloorplanDecorKind.STAGE:
      return {
        width,
        height,
        height3d: 18,
        topFill: 'rgba(55,65,81,0.9)',
        sideFill: 'rgba(31,41,55,0.9)',
        stroke: 'rgba(209,213,219,0.76)',
        accentFill: 'rgba(250,204,21,0.3)',
      }
    case FloorplanDecorKind.RESTROOM:
      return {
        width,
        height,
        height3d: 6,
        topFill: 'rgba(14,116,144,0.62)',
        sideFill: 'rgba(8,145,178,0.64)',
        stroke: 'rgba(165,243,252,0.82)',
        accentFill: 'rgba(207,250,254,0.34)',
      }
    case FloorplanDecorKind.ENTRANCE:
      return {
        width,
        height,
        height3d: 6,
        topFill: 'rgba(71,85,105,0.62)',
        sideFill: 'rgba(51,65,85,0.72)',
        stroke: 'rgba(148,163,184,0.86)',
        accentFill: 'rgba(226,232,240,0.35)',
      }
    default:
      return {
        width,
        height,
        height3d: 4,
        topFill: 'rgba(245,158,11,0.6)',
        sideFill: 'rgba(217,119,6,0.75)',
        stroke: '#f59e0b',
      }
  }
}

export function getRenderableWallLines(
  doc: FloorplanDocument
): RenderableWallLine[] {
  return doc.shell.rooms.flatMap((room) => {
    const entries = [...room.walls, ...room.partitions]
    return entries.flatMap((wall) => {
      const segments = getPolylineSegments(wall.points)
      return segments.map((segment): RenderableWallLine => {
        const [x1, y1, x2, y2] = resolveWorldSegmentFromParent({
          doc,
          points: segment,
          parentId: room.id,
          parentKind: 'room',
        })
        return {
          id: wall.id,
          roomId: room.id,
          points: [x1, y1, x2, y2],
          thickness: getWallThicknessCanvas(wall, doc),
          elevation: getWallHeightCanvas(wall, doc),
          implicit: false,
          wallColor: wall.wallColor ?? null,
          wallMaterial: wall.wallMaterial ?? null,
        }
      })
    })
  })
}

export function seedRoomWallsFromPolygon(
  polygon: number[],
  roomId: string
): Array<{
  id: string
  kind: 'wall'
  points: [number, number, number, number]
  thicknessCm?: number | null
  heightCm?: number | null
  wallColor?: string | null
  wallMaterial?: FloorplanDocument['shell']['rooms'][number]['walls'][number]['wallMaterial']
}> {
  const segments = getPolygonEdgeSegments(polygon)
  return segments.map((segment, index) => ({
    id: `${roomId}_wall_${index + 1}`,
    kind: 'wall',
    points: segment,
    thicknessCm: DEFAULT_WALL_THICKNESS_CM,
    heightCm: 0,
    wallColor: null,
    wallMaterial: null,
  }))
}

export function normalizeLegacyRoomWallIds(
  doc: FloorplanDocument
): FloorplanDocument {
  const ownersByWallId = new Map<string, Set<string>>()
  for (const room of doc.shell.rooms) {
    for (const wall of room.walls) {
      const owners = ownersByWallId.get(wall.id) ?? new Set<string>()
      owners.add(room.id)
      ownersByWallId.set(wall.id, owners)
    }
  }

  const duplicateWallIds = new Set(
    Array.from(ownersByWallId.entries())
      .filter(([, owners]) => owners.size > 1)
      .map(([wallId]) => wallId)
  )
  if (duplicateWallIds.size === 0) return doc

  let changed = false
  const rooms = doc.shell.rooms.map((room) => {
    const wallIdRemap = new Map<string, string>()
    let roomChanged = false
    const walls = room.walls.map((wall, index) => {
      if (!duplicateWallIds.has(wall.id)) return wall
      roomChanged = true
      changed = true
      const nextId = `${room.id}_wall_${index + 1}`
      wallIdRemap.set(wall.id, nextId)
      return { ...wall, id: nextId }
    })
    const openings = room.openings.map((opening) => {
      const nextWallId = wallIdRemap.get(opening.wallId) ?? opening.wallId
      if (nextWallId === opening.wallId) return opening
      roomChanged = true
      changed = true
      return { ...opening, wallId: nextWallId }
    })
    if (!roomChanged) return room
    return { ...room, walls, openings }
  })

  return changed ? { ...doc, shell: { ...doc.shell, rooms } } : doc
}

export function getOpeningWorldRect(args: {
  doc: FloorplanDocument
  roomId: string
  opening: FloorplanDocument['shell']['rooms'][number]['openings'][number]
}): {
  x: number
  y: number
  width: number
  height: number
  rotation: number
} | null {
  const room = args.doc.shell.rooms.find((row) => row.id === args.roomId)
  if (!room) return null
  const wall = [...room.walls, ...room.partitions].find(
    (row) => row.id === args.opening.wallId
  )
  if (!wall) return null
  const [x1, y1, x2, y2] = wall.points
  const worldSegment = resolveWorldSegmentFromParent({
    doc: args.doc,
    points: [x1, y1, x2, y2],
    parentId: room.id,
    parentKind: 'room',
  })
  const [wx1, wy1, wx2, wy2] = worldSegment
  const clampedT = Math.min(1, Math.max(0, args.opening.tAlongWall))
  const width = Math.max(
    1,
    cmToCanvasUnits(args.opening.widthCm ?? 24, args.doc)
  )
  const centerX = wx1 + (wx2 - wx1) * clampedT
  const centerY = wy1 + (wy2 - wy1) * clampedT
  const rotation =
    args.opening.rotation ?? (Math.atan2(wy2 - wy1, wx2 - wx1) * 180) / Math.PI
  return {
    x: centerX,
    y: centerY,
    width,
    height: Math.max(2, width / 4),
    rotation,
  }
}

export function getWallPolygon(
  points: [number, number, number, number],
  thickness: number
): number[] {
  const [x1, y1, x2, y2] = points
  const dx = x2 - x1
  const dy = y2 - y1
  const length = Math.hypot(dx, dy)
  if (length < 0.0001) {
    return [x1, y1, x1, y1, x1, y1, x1, y1]
  }

  const halfThickness = Math.max(0.5, thickness) / 2
  const nx = (-dy / length) * halfThickness
  const ny = (dx / length) * halfThickness

  return [
    x1 + nx,
    y1 + ny,
    x2 + nx,
    y2 + ny,
    x2 - nx,
    y2 - ny,
    x1 - nx,
    y1 - ny,
  ]
}
